import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { RiesgoEBRBreakdown } from "@/components/riesgo/RiesgoEBRBreakdown";
import { GRADO_TONE } from "@/lib/riesgoTokens";
import { api } from "@/lib/api";

function Pill({ children, tone, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone}`}>
      {children}
    </span>
  );
}

// Panel de SOLO LECTURA en la solicitud/Originación.
// - Screening: fósil "reutilizado del titular" (no se reconsulta al proveedor externo).
// - EBR: SÍ se recomputa al vuelo contra la metodología vigente (patrón `perfil-ebr`).
// - Feb-2026: rótulos separados (antes uno solo confundía porque decía "no reconsulta"
//   pero el bloque EBR embebido SÍ recalculaba). Ahora se separa la verdad de cada capa.
// - Prospecto padre: si la solicitud trae `prospecto_id` poblado, el desglose EBR
//   apunta al prospecto (que es la fuente). Si no, apunta al cliente — en cuyo caso
//   el helper `_expediente_efectivo` del backend ya mergea con el prospecto padre
//   cuando existe `origen_prospecto_id`, así que el cliente ve el contexto del padre.
export function SolicitudPldPanel({ solicitud }) {
  const pld = solicitud?.pld_resumen;
  const ebrFosil = solicitud?.ebr_otorgamiento;
  const clienteId = solicitud?.cliente_id;
  const prospectoId = solicitud?.prospecto_id;
  const [vigente, setVigente] = useState(null);
  // Consulta el vigente para poder comparar con el fósil y avisar si difieren.
  useEffect(() => {
    const id = prospectoId || clienteId;
    const tipo = prospectoId ? "prospecto" : "cliente";
    if (!id) return;
    let cancelled = false;
    api.get(`/riesgo/perfil-ebr/${id}`, { params: { tipo } })
      .then((r) => { if (!cancelled) setVigente(r.data?.vigente); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [prospectoId, clienteId]);

  if (!pld && !ebrFosil && !clienteId && !prospectoId) {
    return (
      <div data-testid="solicitud-pld-panel" className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
        Sin evaluación PLD/EBR registrada en esta solicitud (creada antes del rediseño). El resultado vigente del cliente está en su Perfil 360.
      </div>
    );
  }
  // feb-2026 (P0-B) — La UI NO puede pintar "Sin coincidencias" verde sobre un cotejo
  // que nunca completó. Cuando `pld.screening_pendiente===true`, se sustituye el chip
  // por uno ámbar único "Sin verificar — cotejo externo pendiente" con el motivo.
  // Los sub-chips LPB/PEP se OCULTAN porque son afirmaciones sobre datos no obtenidos.
  // El verde sólo aparece cuando el cotejo terminó bien y devolvió cero coincidencias.
  const pendienteReal = !!pld?.screening_pendiente;
  const coincide = !!pld && (pld.coincidencias > 0 || pld.lpb === true || pld.pep === true);
  const cv = solicitud?.coincidencias_vivo;
  // Motivo del pendiente (backend expone `screening_pendiente_motivo` como texto operativo).
  const motivoPend = String(pld?.screening_pendiente_motivo || "").split(":", 1)[0] || "sin_motivo";
  // Discrepancia entre el fósil escrito al crear la solicitud y el cálculo vigente.
  // Muestra transparencia SIN cambiar nada — es lectura pura.
  const discrepancia = !!ebrFosil && !!vigente && (
    (ebrFosil.grado || "").toLowerCase() !== (vigente.grado || "").toLowerCase()
    || Number(ebrFosil.puntaje ?? 0) !== Number(vigente.puntaje_total ?? 0));

  return (
    <div data-testid="solicitud-pld-panel" className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      {/* Bloque 1: Screening PLD — resultado REUTILIZADO del titular (fósil). No se reconsulta al proveedor. */}
      <div className="mb-2 flex items-center gap-2" data-testid="sol-pld-header-screening">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          Screening PLD — resultado reutilizado del titular (no se reconsulta al proveedor)
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-sm">
        {pld && pendienteReal && (
          <Pill testid="sol-pld-sin-verificar" tone="bg-amber-100 text-amber-800 border-amber-300">
            Sin verificar — cotejo externo pendiente ({motivoPend})
          </Pill>
        )}
        {pld && !pendienteReal && (
          <>
            {cv ? (
              <Pill testid="sol-pld-coincidencias"
                tone={cv.confirmadas > 0 || cv.pendientes_relevantes > 0 ? "bg-red-100 text-red-700 border-red-200"
                  : cv.pendientes > 0 ? "bg-amber-100 text-amber-800 border-amber-200"
                  : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
                {cv.confirmadas > 0 ? "Coincidencia confirmada en listas"
                  : cv.pendientes > 0 ? `${cv.pendientes} pendiente(s) de revisión (${cv.pendientes_relevantes} relevante(s), 90% o más)`
                  : "Sin coincidencias pendientes"}
              </Pill>
            ) : (
              <Pill testid="sol-pld-coincidencias" tone={coincide ? "bg-red-100 text-red-700 border-red-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
                {coincide ? `Coincidencias detectadas (${pld.coincidencias})` : "Sin coincidencias"}
              </Pill>
            )}
            {pld.lpb === true && <Pill testid="sol-pld-lpb" tone="bg-red-100 text-red-700 border-red-200">LPB</Pill>}
            {pld.pep === true && <Pill testid="sol-pld-pep" tone="bg-amber-100 text-amber-700 border-amber-200">PEP</Pill>}
          </>
        )}
      </div>
      {clienteId && (
        <a href={`/clientes-360?id=${clienteId}&tab=screening`} className="text-xs text-primary underline">Ver en Perfil 360</a>
      )}
      <p className="mt-1.5 text-[11px] text-slate-400">
        {pendienteReal
          ? "El cotejo contra el proveedor externo no completó (token/saldo/config). El sistema reintenta automáticamente. NO afirmes limpieza sobre datos no obtenidos."
          : "Informativo: no frena el otorgamiento (solo el bloqueo LPB confirmado por el OC). Detalle completo en Cumplimiento PLD y en el Perfil 360 del cliente."}
      </p>

      {/* Bloque 2: Grado EBR — calculado con la metodología vigente al abrir esta pantalla */}
      {(prospectoId || clienteId) && (
        <div className="mt-3 border-t border-slate-200 pt-3" data-testid="sol-pld-header-ebr-wrapper">
          <div className="mb-2 flex items-center gap-2" data-testid="sol-pld-header-ebr">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              Grado EBR — calculado con la metodología vigente al abrir esta pantalla
            </p>
          </div>
          {/* Aviso de discrepancia entre el fósil registrado al crear la solicitud y el vigente */}
          {discrepancia && (
            <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-1.5 text-[11px] text-amber-800"
                 data-testid="sol-ebr-discrepancia">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                El grado registrado al crear la solicitud (<b>{ebrFosil.grado}</b>, {ebrFosil.puntaje ?? 0} pts
                {ebrFosil.evaluado_en ? `, ${String(ebrFosil.evaluado_en).slice(0, 10)}` : ""}) difiere del cálculo vigente
                (<b>{vigente.grado}</b>, {vigente.puntaje_total ?? 0} pts). El vigente refleja los catálogos y datos actuales.
              </span>
            </div>
          )}
          {ebrFosil && (
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5" data-testid="sol-pld-ebr-fosil">
              <span className="text-[11px] text-slate-500">Fósil al crear la solicitud:</span>
              <Pill testid="sol-pld-ebr" tone={GRADO_TONE[ebrFosil.grado] || "bg-slate-100 text-slate-500 border-slate-200"}>
                {ebrFosil.grado || "—"} · {ebrFosil.puntaje ?? 0} pts
              </Pill>
              {ebrFosil.motivo && <span className="text-[11px] text-slate-500">· {ebrFosil.motivo}</span>}
            </div>
          )}
          <RiesgoEBRBreakdown
            docId={prospectoId || clienteId}
            tipo={prospectoId ? "prospecto" : "cliente"}
            canRecalcular={false}
            dense />
        </div>
      )}
    </div>
  );
}
